import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { map, startWith } from 'rxjs';
import {
  COMPANY_OBJECTIVE_KEY_RESULT_TITLE_REJECTION_REASON_LABELS,
  CompanyObjectiveKeyResultTitleRejectionReason,
} from 'src/app/enums/company-objective-key-result-title-rejection-reason.enum';
import { OkrMetricOption, OkrTitleValidationResult } from 'src/app/models/okr-management.model';
import { ApiService } from 'src/app/services/api.service';
import { isOkrTitleValidationError } from './company-objective-form-dialog.component';
import {
  CompanyObjectiveKeyResultMetricSelectorDialogComponent,
  CompanyObjectiveKeyResultMetricSelectorDialogData,
} from './company-objective-key-result-metric-selector-dialog.component';

export interface CompanyObjectiveKeyResultFormDialogData {
  company_product_id: number;
  company_objective_id: number;
  key_result_id?: number;
  objective_title: string;
  level_label: string;
  next_order?: number;
  relatable: 'company' | 'product';
  title?: string;
  category?: string;
  evaluation?: string;
  unit?: string;
  acceptable_target_value?: number;
  good_target_value?: number;
  mode: 'create' | 'edit';
}

@Component({
  selector: 'app-company-objective-key-result-form-dialog',
  templateUrl: './company-objective-key-result-form-dialog.component.html',
  styleUrl: './company-objective-key-result-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    CdkScrollable,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class CompanyObjectiveKeyResultFormDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<CompanyObjectiveKeyResultFormDialogComponent, boolean>);
  private readonly dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly snack = inject(MatSnackBar);
  readonly data = inject<CompanyObjectiveKeyResultFormDialogData>(MAT_DIALOG_DATA);

  readonly saving = signal(false);
  readonly validation = signal<OkrTitleValidationResult | null>(null);
  readonly selected_metric_title = signal<string | null>(null);
  readonly submit_label = computed(() => {
    if (this.saving()) {
      return 'Comprobando Key Result…';
    }

    return this.data.mode === 'edit' ? 'Comprobar Key Result y guardar' : 'Comprobar Key Result y crear';
  });
  readonly rejection_reason_label = computed(() => {
    const result = this.validation();
    if (result === null || result.is_valid || result.rejection_reason === null) {
      return null;
    }

    return (
      COMPANY_OBJECTIVE_KEY_RESULT_TITLE_REJECTION_REASON_LABELS[
        result.rejection_reason as CompanyObjectiveKeyResultTitleRejectionReason
      ] ?? null
    );
  });

  readonly categories = [
    { value: 'usage', label: 'Usage' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'satisfaction', label: 'Satisfaction' },
    { value: 'other', label: 'Other' },
  ];
  readonly evaluations = [
    { value: 'higher_is_better', label: 'Cuanto más, mejor' },
    { value: 'lower_is_better', label: 'Cuanto menos, mejor' },
    { value: 'exact', label: 'Exacto' },
  ];

  readonly form = this.fb.group({
    title: [this.data.title ?? '', Validators.required],
    order: [this.data.next_order ?? 0],
    category: [{ value: this.data.category ?? null, disabled: true }],
    evaluation: [this.data.evaluation ?? 'higher_is_better', Validators.required],
    unit: [this.data.unit ?? '', Validators.required],
    acceptable_target_value: [this.data.acceptable_target_value ?? 0, Validators.required],
    good_target_value: [this.data.good_target_value ?? 0, Validators.required],
    metric_mode: [this.data.mode === 'edit' ? 'existing' : 'new'],
    company_metric_id: [null as number | null],
    metric_title: [''],
  });
  readonly metric_mode = toSignal(this.form.controls.metric_mode.valueChanges, {
    initialValue: this.form.controls.metric_mode.value,
  });
  readonly unit_suffix = toSignal(
    this.form.controls.unit.valueChanges.pipe(startWith(this.form.controls.unit.value ?? '')).pipe(map(unit => (unit ?? '').trim())),
    { initialValue: (this.data.unit ?? '').trim() }
  );
  readonly selected_metric_label = computed(() => this.selected_metric_title() ?? 'Seleccionar métrica');

  applySuggestion(suggestion: string): void {
    this.form.controls.title.setValue(suggestion);
    this.validation.set(null);
  }

  openMetricSelector(): void {
    const data: CompanyObjectiveKeyResultMetricSelectorDialogData = {
      company_product_id: this.data.company_product_id,
      selected_metric_id: this.form.controls.company_metric_id.value,
    };

    this.dialog
      .open<CompanyObjectiveKeyResultMetricSelectorDialogComponent, CompanyObjectiveKeyResultMetricSelectorDialogData, OkrMetricOption>(
        CompanyObjectiveKeyResultMetricSelectorDialogComponent,
        {
          width: '900px',
          maxWidth: '95vw',
          data,
        }
      )
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(metric => {
        if (metric == null) {
          return;
        }

        this.form.controls.company_metric_id.setValue(metric.id);
        this.form.controls.company_metric_id.setErrors(null);
        this.selected_metric_title.set(metric.title);
      });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const use_existing_metric = this.form.controls.metric_mode.value === 'existing';
    if (this.data.mode === 'create') {
      if (use_existing_metric && this.form.controls.company_metric_id.value === null) {
        this.form.controls.company_metric_id.setErrors({ required: true });
        this.form.markAllAsTouched();
        return;
      }
      if (!use_existing_metric && (this.form.controls.metric_title.value ?? '').trim() === '') {
        this.form.controls.metric_title.setErrors({ required: true });
        this.form.markAllAsTouched();
        return;
      }
    }

    const title = this.form.controls.title.value ?? '';
    this.validation.set(null);
    this.saving.set(true);

    let metric_title: string | null = null;
    if (!use_existing_metric && this.data.mode === 'create') {
      metric_title = (this.form.controls.metric_title.value ?? '').trim();
      if (metric_title === '') {
        this.form.controls.metric_title.setErrors({ required: true });
        this.form.markAllAsTouched();
        this.saving.set(false);
        return;
      }
    }

    const request$ =
      this.data.mode === 'edit' && this.data.key_result_id != null
        ? this.api.updateCompanyObjectiveKeyResult(this.data.key_result_id, {
            title,
            order: Number(this.form.controls.order.value),
            evaluation: String(this.form.controls.evaluation.value),
            unit: String(this.form.controls.unit.value),
            acceptable_target_value: Number(this.form.controls.acceptable_target_value.value),
            good_target_value: Number(this.form.controls.good_target_value.value),
          })
        : this.api.storeCompanyObjectiveKeyResult({
            company_objective_id: this.data.company_objective_id,
            title,
            evaluation: String(this.form.controls.evaluation.value),
            unit: String(this.form.controls.unit.value),
            acceptable_target_value: Number(this.form.controls.acceptable_target_value.value),
            good_target_value: Number(this.form.controls.good_target_value.value),
            use_existing_metric,
            company_metric_id: use_existing_metric ? this.form.controls.company_metric_id.value : null,
            metric_title: use_existing_metric ? null : metric_title,
          });

    request$.subscribe({
      next: () => {
        this.saving.set(false);
        this.snack.open(this.data.mode === 'edit' ? 'Se ha actualizado correctamente' : 'Se ha creado correctamente.', 'Ok', {
          duration: 3000,
        });
        this.dialog_ref.close(true);
      },
      error: (error: unknown) => {
        this.saving.set(false);
        if (isOkrTitleValidationError(error)) {
          this.validation.set(error.error);
          return;
        }

        const message =
          error instanceof HttpErrorResponse && error.error?.error
            ? String(error.error.error)
            : 'No se pudo guardar el Key Result. Inténtalo de nuevo.';
        this.snack.open(message, 'Ok', { duration: 3000 });
      },
    });
  }

  close(): void {
    this.dialog_ref.close();
  }
}
